import React, { useState, useMemo } from "react";
import "assets/css/style.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import Modal from "components/ui/Modal";
import Table from "components/ui/Table";
import TableSkeleton from "components/ui/TableSkeleton";
import SearchSelect from "components/ui/SearchSelect";
import CartCorrectionSection from "components/campaignFlow/CartCorrectionSection";
import {
  useCampaignFlowQuery,
  useCampaignFlowByIdQuery,
} from "apis/campaignFlow";

// ── label maps ──
const PAYMENT_LABELS = {
  paid: "Paid",
  inProgress: "In Progress",
  notStarted: "Not Started",
};

const PAYMENT_COLORS = {
  paid: "bg-green-100 text-green-800",
  inProgress: "bg-yellow-100 text-yellow-800",
  notStarted: "bg-lightgray text-darkgray",
};

// Finance option code -> human label (schema: pn / pl / pos). The backend now
// sends financeOptionLabel; this is the fallback for older payloads.
const FINANCE_OPTION_LABELS = {
  pn: "Pay Upfront",
  pl: "Pay Later",
  pos: "Pay on Settlement",
};

// Campaign stage badge colour (mirrors the backend STAGE_LABEL enum).
const STAGE_COLORS = {
  pending: "bg-lightgray text-darkgray",
  created: "bg-lightgray text-darkgray",
  sent: "bg-blue-100 text-blue-800",
  in_finance: "bg-yellow-100 text-yellow-800",
  funded: "bg-green-100 text-green-800",
  live: "bg-blue-100 text-blue-800",
  settlement: "bg-yellow-100 text-yellow-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
  error: "bg-red-100 text-red-800",
};

const INVOICE_STATUS_COLORS = {
  paid: "bg-green-100 text-green-800",
  issued: "bg-yellow-100 text-yellow-800",
  draft: "bg-lightgray text-darkgray",
  voided: "bg-red-100 text-red-800",
};

// Campaign Flow attachment purposes.
const ATTACHMENT_PURPOSE_LABELS = {
  "lending-agreement": "Lending agreement",
  marketing: "Marketing",
  "marketing-vendor": "Marketing (vendor)",
  "property-photo": "Property photo",
  "supporting-doc": "Supporting document",
  "internal-doc": "Internal document",
  "vendor-id": "Vendor ID",
  "upfront-payment-receipt": "Payment receipt",
};

// Event names written by the backend event log.
const EVENT_LABELS = {
  campaign_created: "Campaign created",
  campaign_sent: "Payment link sent",
  campaign_resent: "Payment link re-sent",
  campaign_status_change: "Campaign status change",
  finance_application_status_change: "Finance application status change",
  campaign_budget_funded: "Budget funded",
};

// Stage filter dropdown (maps to the ?stage= query param).
const STAGE_OPTIONS = [
  { value: "", label: "All Stages" },
  { value: "pending", label: "Not Started" },
  { value: "created", label: "Created" },
  { value: "sent", label: "Sent to Vendor" },
  { value: "in_finance", label: "Finance In Progress" },
  { value: "funded", label: "Funded" },
  { value: "live", label: "Live" },
  { value: "settlement", label: "Pending Settlement" },
  { value: "completed", label: "Complete" },
  { value: "cancelled", label: "Cancelled" },
  { value: "error", label: "Error" },
];

// 100 is the server's hard cap on pageSize — asking for more silently clamps.
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-AU", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : "-";

const fmtDateTime = (d) =>
  d
    ? new Date(d).toLocaleString("en-AU", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "-";

export const fmtMoney = (n) =>
  typeof n === "number"
    ? n.toLocaleString("en-AU", {
        style: "currency",
        currency: "AUD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })
    : "-";

const fmtPercent = (n) => (typeof n === "number" ? `${n}%` : "-");

const fmtFileSize = (bytes) => {
  if (typeof bytes !== "number") return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const financeOptionText = (row) =>
  row?.financeOptionLabel ||
  (row?.financeOptionCode
    ? FINANCE_OPTION_LABELS[row.financeOptionCode] || row.financeOptionCode
    : "-");

const humanize = (map, value) =>
  map[value] ||
  String(value || "")
    .replace(/[-_]/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase()) ||
  "-";

// ── small presentational bits ──

const PaymentBadge = ({ bucket, label }) => (
  <span
    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
      PAYMENT_COLORS[bucket] || PAYMENT_COLORS.notStarted
    }`}
  >
    {label || PAYMENT_LABELS[bucket] || "Not Started"}
  </span>
);

const StagePill = ({ stage, label }) => {
  if (!stage) return <span className="text-darkgray">-</span>;
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        STAGE_COLORS[stage] || STAGE_COLORS.pending
      }`}
    >
      {label || stage}
    </span>
  );
};

const YesNoChip = ({ value, yes = "Yes", no = "No" }) => (
  <span
    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
      value ? "bg-green-100 text-green-800" : "bg-lightgray text-darkgray"
    }`}
  >
    {value ? yes : no}
  </span>
);

const CountCard = ({ label, value, dotClass }) => (
  <div className="border border-mediumgray rounded-xl p-4 sm:p-5 bg-white">
    <div className="flex items-center gap-2 text-[11px] sm:text-xs font-semibold uppercase tracking-wide text-darkgray">
      <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${dotClass}`} />
      <span className="truncate">{label}</span>
    </div>
    <div className="text-2xl sm:text-3xl font-bold mt-2 text-black">
      {value}
    </div>
  </div>
);

// ── detail modal content ──

const DetailField = ({ label, value, pre }) => (
  <div className="mb-4">
    <div className="text-xs font-semibold uppercase tracking-wide text-darkgray mb-1">
      {label}
    </div>
    <div className={`text-sm text-black ${pre ? "whitespace-pre-line" : ""}`}>
      {value || value === 0 ? value : "-"}
    </div>
  </div>
);

export const Section = ({ title, count, children }) => (
  <div className="border-t border-mediumgray mt-5 pt-5">
    <div className="text-base font-bold mb-3 text-black">
      {title}
      {count === undefined ? "" : ` (${count})`}
    </div>
    {children}
  </div>
);

// Compact table used for budget items, invoices, payments, docs, events, and
// (from components/campaignFlow/CartCorrectionSection) cart line items.
export const MiniTable = ({ head, rows }) => (
  <div className="border border-mediumgray rounded-xl overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-lightgray text-left text-[11px] uppercase tracking-wide text-darkgray">
          {head.map((h) => (
            <th key={h} className="px-3 py-2 font-medium whitespace-nowrap">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((cells, i) => (
          <tr key={i} className="border-t border-lightgray">
            {cells.map((c, j) => (
              <td key={j} className="px-3 py-2 align-top whitespace-nowrap">
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// The vendor-facing milestone tick-list, as Campaign Flow shows it.
const Timeline = ({ steps }) => (
  <div>
    {steps.map((s, i, arr) => (
      <div key={s.key} className="relative pl-6 pb-4 last:pb-0">
        {i !== arr.length - 1 && (
          <span
            className="absolute left-[5px] top-4 bottom-0 w-px bg-mediumgray"
            aria-hidden="true"
          />
        )}
        <span
          className={`absolute left-0 top-1 w-[11px] h-[11px] rounded-full border-2 ${
            s.done ? "border-green-600 bg-green-600" : "border-mediumgray bg-white"
          }`}
        />
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span
            className={`text-sm ${
              s.done ? "font-medium text-black" : "text-darkgray"
            }`}
          >
            {s.label}
          </span>
          {s.at && (
            <span className="text-xs text-darkgray">{fmtDateTime(s.at)}</span>
          )}
        </div>
      </div>
    ))}
  </div>
);

const VendorCard = ({ vendor }) => (
  <div className="border border-mediumgray rounded-xl p-4 mb-3">
    <div className="text-base font-bold text-black">{vendor.name || "-"}</div>
    <div className="text-sm text-darkgray mb-3">
      {[vendor.email, vendor.mobile].filter(Boolean).join(" · ") || "-"}
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-darkgray mb-1.5">
          Link Sent
        </div>
        <YesNoChip value={vendor.sentTo} />
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-darkgray mb-1.5">
          Completed
        </div>
        <YesNoChip value={vendor.completed} />
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-darkgray mb-1.5">
          Contact Verified
        </div>
        <YesNoChip
          value={!!(vendor.emailVerifiedAt || vendor.mobileVerifiedAt)}
          yes={vendor.emailVerifiedAt && vendor.mobileVerifiedAt ? "Both" : "Partial"}
          no="No"
        />
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-darkgray mb-1.5">
          AML
        </div>
        <YesNoChip value={vendor.amlCheckCompleted} yes="Done" no="Pending" />
      </div>
    </div>
    {vendor.isProceedingUnderSoleName && (
      <p className="text-xs text-darkgray mt-3">
        Proceeding as the sole signatory on the finance application.
      </p>
    )}
  </div>
);

const DetailSkeleton = () => (
  <div>
    {Array.from({ length: 7 }).map((_, i) => (
      <div className="mb-4" key={i}>
        <Skeleton width={120} height={10} />
        <Skeleton width="60%" height={16} />
      </div>
    ))}
    <div className="border-t border-mediumgray mt-5 pt-5">
      <Skeleton width={160} height={18} className="mb-3" />
      <div className="border border-mediumgray rounded-xl p-4">
        <Skeleton width={140} height={18} />
        <Skeleton width={180} height={12} className="mb-3" />
        <div className="grid grid-cols-2 gap-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i}>
              <Skeleton width={90} height={10} />
              <Skeleton height={24} borderRadius={999} />
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// One campaign on a property that has several. Cart 1 rides the primary
// campaign; each later cart is funded by its own, which is what the vendor
// sees as a second entry on Campaign Flow's dashboard.
const CampaignCard = ({ campaign }) => (
  <div className="border border-mediumgray rounded-xl p-4">
    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
      <div>
        <span className="font-medium">
          Cart {campaign.sequence} — {fmtMoney(campaign.budgetAmount)}
        </span>
        <span className="text-xs text-darkgray ml-2">
          {campaign.isPrimary
            ? "primary campaign"
            : campaign.folded
              ? "topped up onto the primary loan"
              : "own campaign"}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <PaymentBadge bucket={campaign.payment} label={campaign.paymentLabel} />
        <StagePill stage={campaign.stage} label={campaign.stageLabel} />
      </div>
    </div>

    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <DetailField label="Campaign ID" value={campaign.campaignId} />
      <DetailField label="Budget ID" value={campaign.budgetId} />
      <DetailField label="Campaign Status" value={campaign.campaignStatusLabel} />
      <DetailField label="Finance" value={financeOptionText(campaign)} />
    </div>

    {campaign.vendorUrl && (
      <div className="mb-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-darkgray mb-1">
          Vendor URL
        </div>
        <a
          href={campaign.vendorUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-blue-600 underline break-all"
        >
          {campaign.vendorUrl}
        </a>
      </div>
    )}

    {campaign.timeline?.length > 0 && <Timeline steps={campaign.timeline} />}
  </div>
);

const CampaignFlowDetail = ({ id }) => {
  const { data: detail, isPending } = useCampaignFlowByIdQuery(id);

  if (isPending) return <DetailSkeleton />;
  if (!detail) return <p className="py-4">No detail available.</p>;

  const pay = detail.paymentDetail || {};
  const campaign = detail.campaignDetail || {};
  const finance = detail.financeDetail;
  const timeline = detail.timeline || [];
  const campaigns = detail.campaigns || [];
  const budgetItems = detail.budgetItems || [];
  const invoices = detail.invoices || [];
  const attachments = detail.attachments || [];
  const events = detail.events || [];

  // Every payment across every invoice, newest first — the receipt trail.
  const payments = invoices.flatMap((inv) =>
    (inv.payments || []).map((p) => ({ ...p, invoiceId: inv.invoiceId })),
  );

  const priceGuide =
    campaign.sellingPriceMin || campaign.sellingPriceMax
      ? `${fmtMoney(campaign.sellingPriceMin)} – ${fmtMoney(campaign.sellingPriceMax)}`
      : null;

  return (
    <div>
      <DetailField label="Property Address" value={detail.address} />
      <DetailField label="Date Created" value={fmtDate(detail.dateCreated)} />
      <DetailField label="Agency Office" value={detail.agencyOffice} />
      <DetailField label="Agent" value={detail.agent} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <DetailField label="Campaign ID" value={detail.campaignId} />
        <DetailField label="Budget ID" value={detail.budgetId} />
        <DetailField
          label="Campaign Status"
          value={detail.campaignStatusLabel}
        />
        <DetailField label="Payment" value={detail.paymentLabel} />
      </div>

      {detail.actionSuggested && (
        <div className="bg-yellow-50 text-yellow-800 rounded-lg p-3 text-sm mb-4">
          Campaign Flow has flagged this campaign as needing agent action.
        </div>
      )}

      {/* ── Every campaign on this property ── */}
      {campaigns.length > 1 && (
        <Section title="Campaigns" count={campaigns.length}>
          <p className="text-sm text-darkgray mb-3">
            Cart 1 rides the primary campaign; each later cart is funded by its
            own. Everything below this section — Budget, Timeline, Payment,
            Invoices — describes the primary campaign only.
          </p>
          <div className="space-y-3">
            {campaigns.map((c) => (
              <CampaignCard key={c.sequence} campaign={c} />
            ))}
          </div>
        </Section>
      )}

      {/* ── Timeline ── */}
      {timeline.length > 0 && (
        <Section title="Timeline">
          <Timeline steps={timeline} />
        </Section>
      )}

      {/* ── Budget ── */}
      <Section title="Budget">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <DetailField
            label="Budget Amount"
            value={fmtMoney(detail.budgetAmount)}
          />
          <DetailField label="Cart Total" value={fmtMoney(detail.cartTotal)} />
          <DetailField
            label="Agent Contribution"
            value={fmtMoney(detail.agentContribution)}
          />
          <DetailField
            label="Vendor Payable"
            value={fmtMoney(detail.totalPayable)}
          />
        </div>
        <DetailField
          label="Budget Description"
          value={detail.budgetDescription}
          pre
        />
        {budgetItems.length > 0 && (
          <div className="mb-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-darkgray mb-1.5">
              Budget Items
            </div>
            <MiniTable
              head={["Description", "Category", "Amount", "Discount"]}
              rows={budgetItems.map((i) => [
                i.description || "-",
                i.category || "-",
                fmtMoney(i.amount),
                i.discount ? `- ${fmtMoney(i.discount)}` : "-",
              ])}
            />
          </div>
        )}
        {detail.plannedStartDate && (
          <DetailField
            label="Planned Start"
            value={fmtDate(detail.plannedStartDate)}
          />
        )}
        {detail.vendorUrl && (
          <div className="mb-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-darkgray mb-1">
              Vendor URL
            </div>
            <a
              href={detail.vendorUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 underline break-all"
            >
              {detail.vendorUrl}
            </a>
          </div>
        )}
      </Section>

      {/* ── Cart correction (admin-only swap, gated by Campaign Flow tier) ── */}
      <Section title="Cart">
        <CartCorrectionSection propertyId={detail.propertyId} />
      </Section>

      {/* ── Payment ── */}
      <Section title="Payment">
        {detail.isPaid && pay.receiptNumber && (
          <div className="bg-green-50 text-green-800 rounded-lg p-3 text-sm mb-4">
            Paid in full by {pay.methodLabel || pay.method || "unknown method"} —
            receipt <span className="font-semibold">{pay.receiptNumber}</span>
            {pay.paidAt ? ` on ${fmtDate(pay.paidAt)}` : ""}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="mb-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-darkgray mb-1.5">
              Paid
            </div>
            <YesNoChip value={detail.isPaid} />
          </div>
          <DetailField label="Finance Option" value={financeOptionText(detail)} />
          <DetailField
            label="Upfront Payment Method"
            value={detail.upfrontPaymentMethodLabel}
          />
          <DetailField
            label="Finance Application Status"
            value={detail.financeApplicationStatusLabel}
          />
          <DetailField label="Amount Paid" value={fmtMoney(pay.amount)} />
          <DetailField label="Surcharge" value={fmtMoney(pay.surcharge)} />
          <DetailField label="Paid At" value={fmtDateTime(pay.paidAt)} />
          <DetailField label="Funded Date" value={fmtDate(detail.fundedDate)} />
          <DetailField
            label="Completed At"
            value={fmtDate(detail.completedAt)}
          />
        </div>
      </Section>

      {/* ── Finance application (pay later / pay on settlement only) ── */}
      {finance && (
        <Section title="Finance Application">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <DetailField label="Application ID" value={finance.applicationId} />
            <DetailField label="Status" value={finance.statusLabel} />
            <DetailField
              label="Amount Financed"
              value={fmtMoney(finance.amount)}
            />
            <DetailField
              label="Vendor Service Fee"
              value={fmtMoney(finance.retailServiceFee)}
            />
            <DetailField
              label="Agency Fee"
              value={fmtMoney(finance.budgetOwnerFee)}
            />
            <DetailField label="Caveat Fee" value={fmtMoney(finance.caveatFee)} />
            <DetailField
              label="Interest Rate"
              value={
                typeof finance.interestRate === "number"
                  ? `${fmtPercent(finance.interestRate)}${
                      finance.interestRateIsVariable ? " (variable)" : ""
                    }`
                  : "-"
              }
            />
            <DetailField
              label="Interest Free Days"
              value={finance.interestFreeDays}
            />
            <DetailField
              label="Comparison Rate"
              value={fmtPercent(finance.comparisonRate)}
            />
            <DetailField
              label="Submitted"
              value={fmtDate(finance.submitDate)}
            />
            <DetailField label="Approved" value={fmtDate(finance.approvedDate)} />
            <div className="mb-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-darkgray mb-1.5">
                Top Up
              </div>
              <YesNoChip value={finance.isTopUp} />
            </div>
          </div>

          {(finance.signatories || []).length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-darkgray mb-1.5">
                Signatories
              </div>
              <MiniTable
                head={["Name", "Contact", "Signed", "Role"]}
                rows={finance.signatories.map((s) => [
                  s.name || "-",
                  [s.email, s.mobile].filter(Boolean).join(" · ") || "-",
                  s.signedAt ? fmtDate(s.signedAt) : "Not signed",
                  [
                    s.isGuarantor ? "Guarantor" : null,
                    s.isOwnershipEntityRep ? "Entity rep" : null,
                  ]
                    .filter(Boolean)
                    .join(", ") || "Vendor",
                ])}
              />
            </div>
          )}
        </Section>
      )}

      {/* ── Invoices & payments ── */}
      {invoices.length > 0 && (
        <Section title="Invoices" count={invoices.length}>
          <MiniTable
            head={["Invoice", "Type", "Status", "Amount", "Paid", "Due"]}
            rows={invoices.map((inv) => [
              inv.invoiceId,
              inv.type || "-",
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  INVOICE_STATUS_COLORS[inv.status] || "bg-lightgray text-darkgray"
                }`}
              >
                {inv.status || "-"}
              </span>,
              fmtMoney(inv.amount),
              fmtMoney(inv.paidAmount),
              fmtDate(inv.dueDate),
            ])}
          />
          {payments.length > 0 && (
            <div className="mt-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-darkgray mb-1.5">
                Payments
              </div>
              <MiniTable
                head={["Date", "Method", "Amount", "Surcharge", "Receipt", "Status"]}
                rows={payments.map((p) => [
                  fmtDate(p.paymentDate),
                  p.methodLabel || p.method || "-",
                  fmtMoney(p.amount),
                  fmtMoney(p.surcharge),
                  p.receiptNumber || "-",
                  p.status || "-",
                ])}
              />
            </div>
          )}
        </Section>
      )}

      {/* ── Documents ── */}
      {attachments.length > 0 && (
        <Section title="Documents" count={attachments.length}>
          <MiniTable
            head={["Purpose", "File", "Size", "Uploaded"]}
            rows={attachments.map((a) => [
              humanize(ATTACHMENT_PURPOSE_LABELS, a.purpose),
              a.fileName || "-",
              fmtFileSize(a.fileSize) || "-",
              fmtDate(a.uploadDate),
            ])}
          />
        </Section>
      )}

      {/* ── Campaign detail from Campaign Flow ── */}
      <Section title="Campaign">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <DetailField label="Price Guide" value={priceGuide} />
          <DetailField label="Sold Price" value={fmtMoney(campaign.soldPrice)} />
          <DetailField label="Sale Method" value={campaign.saleMethod} />
          <DetailField
            label="Auction Date"
            value={campaign.auctionDate ? fmtDate(campaign.auctionDate) : null}
          />
          <DetailField
            label="Settlement Date"
            value={
              campaign.settlementDate ? fmtDate(campaign.settlementDate) : null
            }
          />
          <DetailField
            label="Loan End Date"
            value={campaign.loanEndDate ? fmtDate(campaign.loanEndDate) : null}
          />
          <DetailField label="AML Status" value={campaign.amlStatus} />
          <DetailField
            label="Status Last Updated"
            value={fmtDateTime(campaign.statusLastUpdate)}
          />
        </div>
        {campaign.cancelledReason && (
          <div className="bg-red-50 text-red-700 rounded-lg p-3 text-sm mb-4">
            Cancelled ({campaign.cancelledReason})
            {campaign.cancelledDate ? ` on ${fmtDate(campaign.cancelledDate)}` : ""}
            {campaign.cancelledNotes ? ` — ${campaign.cancelledNotes}` : ""}
          </div>
        )}
        {campaign.agentNotes && (
          <DetailField label="Agent Notes" value={campaign.agentNotes} pre />
        )}
        {campaign.notes && (
          <DetailField label="Notes" value={campaign.notes} pre />
        )}
      </Section>

      {/* ── Vendors ── */}
      <Section title="Vendors" count={detail.vendors?.length || 0}>
        {(detail.vendors || []).map((v, i) => (
          <VendorCard key={v.vendorId || i} vendor={v} />
        ))}
        {(!detail.vendors || detail.vendors.length === 0) && (
          <p className="text-darkgray">No vendors on this campaign yet.</p>
        )}
      </Section>

      {/* ── Activity ── */}
      {events.length > 0 && (
        <Section title="Activity" count={events.length}>
          <MiniTable
            head={["Time", "Event", "Status", "Source"]}
            rows={[...events].reverse().map((e) => [
              fmtDateTime(e.at),
              humanize(EVENT_LABELS, e.event),
              e.financeApplicationStatus || e.campaignStatus || "-",
              e.source || "-",
            ])}
          />
        </Section>
      )}

      {/* ── Sync info ── */}
      <Section title="Sync">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <DetailField label="Sent At" value={fmtDateTime(detail.sentAt)} />
          <DetailField
            label="Last Synced"
            value={fmtDateTime(detail.lastSyncedAt)}
          />
          <DetailField
            label="Last Webhook Event"
            value={
              detail.lastWebhookEvent
                ? humanize(EVENT_LABELS, detail.lastWebhookEvent)
                : null
            }
          />
          <DetailField label="Wizard Step" value={detail.wizardStep} />
        </div>
      </Section>

      {detail.lastError && (
        <div className="bg-red-50 text-red-700 rounded-lg p-3 text-sm mt-4">
          Last error: {detail.lastError}
        </div>
      )}
    </div>
  );
};

// ── filter bar (module-level, not recreated each render) ──

const FilterBar = ({ tab, onTabChange, stageOption, onStageChange }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 w-full mb-4">
    <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
      {[
        { key: "", label: "All" },
        { key: "paid", label: "Paid" },
        { key: "inProgress", label: "In Progress" },
        { key: "notStarted", label: "Not Started" },
      ].map((t) => (
        <button
          key={t.key}
          onClick={() => onTabChange(t.key)}
          className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap shrink-0 ${
            tab === t.key ? "bg-black text-white" : "bg-lightgray text-darkgray"
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
    <div className="w-full sm:w-52 sm:ml-auto shrink-0">
      <SearchSelect
        options={STAGE_OPTIONS}
        value={stageOption}
        onChange={(opt) => onStageChange(opt || STAGE_OPTIONS[0])}
        searchable={false}
        placeholder="All Stages"
      />
    </div>
  </div>
);

// ── main page ──

const CampaignFlow = () => {
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState("");
  const [stageOption, setStageOption] = useState(STAGE_OPTIONS[0]);
  // Search text arrives from the table already debounced and trimmed.
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(1);

  const params = useMemo(
    () => ({
      status: tab || undefined,
      stage: stageOption?.value || undefined,
      search: search || undefined,
      page,
      pageSize,
    }),
    [tab, stageOption, search, page, pageSize],
  );

  // Filter changes must reset to page 1, or you land on an out-of-range page.
  const handleTabChange = (key) => {
    setTab(key);
    setPage(1);
  };
  const handleStageChange = (opt) => {
    setStageOption(opt || STAGE_OPTIONS[0]);
    setPage(1);
  };
  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };
  const handleSearchChange = (query) => {
    setSearch(query);
    setPage(1);
  };

  const {
    data,
    isPending: isDataPending,
    isError: isDataError,
    error: dataError,
  } = useCampaignFlowQuery(params);

  const counts = data?.counts || {};
  const rows = data?.data || [];
  const pagination = data?.pagination || null;

  const columns = useMemo(
    () => [
      {
        accessorKey: "dateCreated",
        header: "Date Created",
        cell: (row) => (
          <span className="text-sm">{fmtDate(row.dateCreated)}</span>
        ),
        isFilter: false,
        minWidth: "140px",
      },
      {
        accessorKey: "address",
        header: "Property Address",
        // One row per property, but a property can hold several campaigns —
        // cart 1 on the primary, each later cart on its own. The row's money
        // and status cover all of them, so it has to say how many there are.
        cell: (row) => (
          <div>
            <div className="font-medium">{row.address}</div>
            {row.campaignCount > 1 && (
              <div className="text-xs text-darkgray mt-0.5">
                {row.campaignCount} campaigns
              </div>
            )}
          </div>
        ),
        isFilter: true,
        minWidth: "220px",
      },
      {
        accessorKey: "agencyOffice",
        header: "Agency Office",
        cell: (row) => (
          <span className="text-sm text-darkgray">
            {row.agencyOffice || "-"}
          </span>
        ),
        isFilter: true,
      },
      {
        accessorKey: "agent",
        header: "Agent",
        cell: (row) => (
          <span className="text-sm text-darkgray">{row.agent || "-"}</span>
        ),
        isFilter: true,
      },
      {
        accessorKey: "budgetAmount",
        header: "Budget",
        // Budget is the net figure the vendor funds, summed across every
        // campaign on the property; payable normally equals it and is only
        // shown when CF's live total differs (CF-side fees).
        cell: (row) => (
          <div className="text-sm">
            <div>{fmtMoney(row.budgetAmount)}</div>
            {row.campaignCount > 1 ? (
              <div className="text-xs text-darkgray">
                across {row.campaignCount} campaigns
              </div>
            ) : (
              typeof row.totalPayable === "number" &&
              row.totalPayable !== row.budgetAmount && (
                <div className="text-xs text-darkgray">
                  payable {fmtMoney(row.totalPayable)}
                </div>
              )
            )}
          </div>
        ),
        isFilter: false,
        minWidth: "130px",
      },
      {
        accessorKey: "financeOptionCode",
        header: "Finance",
        cell: (row) => (
          <span className="text-sm text-darkgray">
            {financeOptionText(row)}
          </span>
        ),
        isFilter: false,
        minWidth: "130px",
      },
      {
        accessorKey: "payment",
        header: "Payment",
        cell: (row) => (
          <div>
            <PaymentBadge bucket={row.payment} label={row.paymentLabel} />
            {row.receiptNumber && (
              <div className="text-xs text-darkgray mt-1">
                #{row.receiptNumber}
              </div>
            )}
          </div>
        ),
        isFilter: false,
      },
      {
        accessorKey: "stage",
        header: "Stage",
        cell: (row) => <StagePill stage={row.stage} label={row.stageLabel} />,
        isFilter: false,
        minWidth: "140px",
      },
    ],
    [],
  );

  if (isDataError) {
    return (
      <div className="container mx-auto px-4 sm:px-0">
        <div className="text-center py-8">
          <p className="text-red-600">
            Error loading Campaign Flow dashboard: {dataError?.message}
          </p>
        </div>
      </div>
    );
  }

  const handleRowClick = (row) => {
    setSelectedId(row.id);
    setIsViewOpen(true);
  };

  const selectedAddress =
    rows.find((r) => r.id === selectedId)?.address || "Campaign Details";

  return (
    <>
      <Modal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title={selectedAddress}
        className="max-w-3xl"
      >
        {selectedId && <CampaignFlowDetail id={selectedId} />}
      </Modal>

      <div className="container mx-auto px-4 sm:px-0">
        {/* Header */}
        <div className="mb-6">
          <div className="text-xs font-bold uppercase tracking-wider text-black">
            Campaign Flow Dashboard
          </div>
          <div className="text-darkgray mt-1">
            Track vendor-paid advertising campaigns and payment status across
            your organisation
          </div>
        </div>

        {/* Count cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <CountCard
            label="Total"
            value={counts.total ?? "-"}
            dotClass="bg-black"
          />
          <CountCard
            label="Paid"
            value={counts.paid ?? "-"}
            dotClass="bg-green-500"
          />
          <CountCard
            label="In Progress"
            value={counts.inProgress ?? "-"}
            dotClass="bg-yellow-500"
          />
          <CountCard
            label="Not Started"
            value={counts.notStarted ?? "-"}
            dotClass="bg-darkgray"
          />
        </div>

        <div>
          <h4 className="mb-4">Campaigns</h4>
          <FilterBar
            tab={tab}
            onTabChange={handleTabChange}
            stageOption={stageOption}
            onStageChange={handleStageChange}
          />
          {isDataPending ? (
            <TableSkeleton
              rows={Math.min(pageSize, 10)}
              columns={columns.length}
            />
          ) : (
            <Table
              columns={columns}
              data={rows}
              pageSize={pageSize}
              onRowClick={handleRowClick}
              searchPlaceholder="Search property address, agent or office..."
              onSearchChange={handleSearchChange}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onPageSizeChange={handlePageSizeChange}
              emptyText={
                search ? "No campaigns match your search" : "No campaigns found"
              }
              serverPagination={
                pagination
                  ? {
                      page: pagination.page,
                      totalPages: pagination.totalPages,
                      total: pagination.total,
                      limit: pagination.pageSize,
                    }
                  : null
              }
              onPageChange={setPage}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default CampaignFlow;
